import React, { useEffect, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import DropdownMenu from "../../ui/DropdownMenu";
import { MoreVertical } from "lucide-react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import ConfirmDeleteCouponModal from "./modals/DeleteCoupon";
import {
  fetchAllCoupons,
  fetchAllUsedCoupons,
} from "../../../redux/features/CouponSlice";

interface UsedCouponsTableProps {
  page: number;
  limit: number;
  setPage: (value: number) => void;
  setLimit: (value: number) => void;
}

const UsedCouponsTable: React.FC<UsedCouponsTableProps> = ({
  page,
  limit,
  setPage,
  setLimit,
}) => {
  const dispatch = useAppDispatch();
  const { usedCoupons, loading } = useAppSelector((state) => state.coupons);

  const [selectedCoupon, setSelectedCoupon] = useState<any>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const dropdownOptionsForCoupon = [
    {
      label: <span className="text-red-500">Delete</span>,
      onClick: (coupon: any) => {
        setSelectedCoupon(coupon);
        setDeleteModalOpen(true);
      },
      returnAll: true,
    },
  ];

  const couponData = {
    columns: [
      { label: "Code", field: "code" },
      { label: "Coupon Type", field: "type" },
      { label: "Assigned To", field: "assigned_to" },
      { label: "Email", field: "email" },
      { label: "Value", field: "value" },
      { label: "Expires", field: "expiryDate" },
      { label: "Status", field: "status" },
      { label: "Action", field: "action" },
    ],
    rows: usedCoupons?.data?.items?.map((item: any, index: number) => {
      const isExpired = new Date(item?.coupon?.expiryDate) < new Date();
      const value =
        item?.coupon?.couponAmount !== null
          ? `₦${item?.coupon?.couponAmount}`
          : item?.coupon?.couponDiscount !== null
          ? `${item?.couponDiscount}%`
          : "N/A";

      return {
        id: index + 1,
        code: item?.coupon?.couponCode,
        type: item?.coupon?.couponType,
        email: item?.assignedTo?.email,
        assigned_to:
          item?.assignedTo?.lastName + " " + item?.assignedTo?.firstName,
        value,
        expiryDate: moment(item?.expiryDate).format("Do MMM YYYY"),
        status: (
          <span
            className={`text-xs font-semibold px-3 py-1 rounded-full ${
              isExpired
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {isExpired ? "Expired" : "Active"}
          </span>
        ),
        action: (
          <DropdownMenu
            options={dropdownOptionsForCoupon}
            icon={<MoreVertical style={{ cursor: "pointer" }} />}
            item={item}
          />
        ),
      };
    }),
  };

  useEffect(() => {
    dispatch(fetchAllUsedCoupons({ page: page + 1, limit }));
  }, [dispatch, page, limit]);

  return (
    <>
      <CustomTable
        data={couponData}
        noRecordText="No Coupons Available"
        loading={loading}
        showFooter={true}
        totalPages={usedCoupons?.meta?.totalPages}
        itemsPerPage={usedCoupons?.meta?.itemsPerPage}
        currentPage={page}
        onPageChange={(value: number) => setPage(value)}
        onLimitChange={(value: number) => {
          setLimit(value);
          setPage(0);
        }}
      />

      <ConfirmDeleteCouponModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        couponId={selectedCoupon?.id}
        onDeleted={() => dispatch(fetchAllCoupons({ page: page + 1, limit }))}
      />
    </>
  );
};

export default UsedCouponsTable;
